import React from 'react';
import './CoachingPanel.css';

const ISSUE_META = {
  nervous:     { label: 'Nerves',          icon: '💓' },
  eye_contact: { label: 'Eye Contact',     icon: '👁' },
  speech_fast: { label: 'Pace (too fast)', icon: '⚡' },
  speech_slow: { label: 'Pace (too slow)', icon: '🐢' },
  filler:      { label: 'Filler Words',    icon: '💬' },
  silence:     { label: 'Long Silence',    icon: '🔇' },
};

const ISSUE_ORDER = ['nervous', 'eye_contact', 'filler', 'speech_fast', 'speech_slow', 'silence'];

function severityColor(s) {
  if (s >= 1.0) return '#ef4444';
  if (s >= 0.6) return '#fbbf24';
  if (s >= 0.2) return '#f97316';
  return '#2a2a2a';
}

function liveSeverities(issues, stats, liveWpm, liveFillerPct) {
  const wpm = liveWpm > 0 ? liveWpm : (stats?.wpm ?? 0);
  return {
    ...issues,
    speech_fast: wpm > 160  ? Math.min(1, (wpm - 160) / 100)  : 0,
    speech_slow: wpm > 0 && wpm < 100 ? Math.min(1, (100 - wpm) / 100) : 0,
    silence: stats?.silence > 3 ? Math.min(1, (stats.silence - 3) / 7) : 0,
    // Use live filler count from interim results when available
    filler: liveFillerPct >= 0
      ? Math.min(1, liveFillerPct / 30)
      : (stats?.fillerWords > 0 ? Math.min(1, stats.fillerWords / 30) : (issues.filler ?? 0)),
  };
}

function CoachingPanel({ issues = {}, interruption = null, isRunning, stats = {}, liveWpm = 0, liveFillerPct = -1 }) {
  if (!isRunning) return null;

  const effective    = liveSeverities(issues, stats, liveWpm, liveFillerPct);
  const hasActiveTip = interruption?.active && interruption?.message;
  const noFaceActive = effective.no_face >= 1.0;

  return (
    <div className={`coaching-panel${hasActiveTip ? ' coaching-panel--alert' : ''}`}>
      <div className="coaching-panel-header">
        <div className="coaching-panel-title">
          <span className="ai-badge">AI</span>
          coaching engine
        </div>
        <span className={`coaching-status ${hasActiveTip ? 'status-alert' : 'status-ok'}`}>
          {hasActiveTip ? '● tip active' : '● monitoring'}
        </span>
      </div>

      {noFaceActive && (
        <div className="no-face-warning">
          📷 Face not visible — move into frame
        </div>
      )}

      {hasActiveTip && (
        <div className="coaching-tip" key={interruption.message}>
          <div className="tip-icon">💡</div>
          <div className="tip-body">
            <div className="tip-label">coaching tip</div>
            <p className="tip-message">{interruption.message}</p>
          </div>
        </div>
      )}

      <div className="coaching-issues">
        {ISSUE_ORDER.map(key => {
          const severity = effective[key] ?? 0;
          const color    = severityColor(severity);
          const meta     = ISSUE_META[key];
          return (
            <div key={key} className="coaching-issue">
              <span className="issue-icon">{meta.icon}</span>
              <span className="issue-label">{meta.label}</span>
              <div className="issue-bar-track">
                <div
                  className="issue-bar-fill"
                  style={{ width: `${Math.round(severity * 100)}%`, background: color }}
                />
              </div>
              <span className="issue-pct" style={{ color: severity > 0.2 ? color : '#444' }}>
                {Math.round(severity * 100)}%
              </span>
            </div>
          );
        })}
      </div>

      <div className="coaching-footer">
        Q-Learning · PyTorch CNN · OpenCV
      </div>
    </div>
  );
}

export default CoachingPanel;
